import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { LoggerService } from '../services/logger.service';

/**
 * Exhaustive HTTP logging interceptor for Angular.
 * Captures EVERY outgoing request and incoming response (both success and failure),
 * attaches correlation ID (X-Request-Id), and safely masks sensitive parameters.
 */
export const loggingInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const logger = inject(LoggerService);

  // Skip logging internal log ingestion calls or explicit bypass to prevent infinite recursion
  if (req.url.includes('/api/logs/') || req.headers.has('X-Logging-Internal')) {
    return next(req);
  }

  // 1. Resolve or generate unique correlation ID
  const existingRequestId = req.headers.get('X-Request-Id');
  const requestId = existingRequestId || logger.generateRequestId();

  // Attach X-Request-Id header to outgoing request
  const clonedRequest = req.clone({
    headers: req.headers.set('X-Request-Id', requestId)
  });

  const startTime = Date.now();

  // 2. Extract request headers and mask authorization/sensitive data
  const headers: Record<string, string> = {};
  clonedRequest.headers.keys().forEach(key => {
    const val = clonedRequest.headers.get(key) || '';
    if (key.toLowerCase().includes('authorization') || key.toLowerCase().includes('token')) {
      headers[key] = val.startsWith('Bearer ') ? 'Bearer ***MASKED***' : '***MASKED***';
    } else {
      headers[key] = val;
    }
  });

  // Pipe and only log Errors (skip logging successful HTTP calls to prevent log bloat)
  return next(clonedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      const durationMs = Date.now() - startTime;
      logger.log({
        level: 'ERROR',
        message: `HTTP Error ${clonedRequest.method} ${clonedRequest.urlWithParams} ${error.status} (${durationMs}ms): ${error.message}`,
        requestId,
        url: clonedRequest.urlWithParams,
        method: clonedRequest.method,
        status: error.status,
        durationMs,
        responseBody: error.error,
        context: {
          statusText: error.statusText,
          errorName: error.name,
          headers
        }
      });
      return throwError(() => error);
    })
  );
};
